import { camelize, snake } from '../helpers/converterString';

export const DEFAULT_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

export type RequestArgs = { [key: string]: any } | null | undefined;

export default class Controller {
  static get(
    route: string,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
    signal: AbortSignal | null = null,
  ) {
    const url = Controller.makeUrl(route, args);
    return fetch(url, {
      method: 'GET',
      signal,
      headers,
    })
      .then(response => Controller.handleFailedResponse(response))
      .then(response => {
        if (response.url.split('?')[0].slice(-3) === 'csv') {
          return response.blob();
        }
        return response.json();
      })
      .then(camelize);
  }

  static put(
    route: string,
    body,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
    skipSnake?: boolean,
    signal: AbortSignal | null = null,
  ) {
    const url = Controller.makeUrl(route, args);

    return fetch(url, {
      method: 'PUT',
      headers,
      signal,
      body: skipSnake ? JSON.stringify(body) : JSON.stringify(snake(body)),
    })
      .then(response => Controller.handleFailedResponse(response))
      .then(response => response.json().catch(() => response))
      .then(camelize);
  }

  static async post(
    route: string,
    body,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
    signal: AbortSignal | null = null,
    withCredentials: boolean = false,
    skipSnake?: boolean,
  ) {
    const url = Controller.makeUrl(route, args);
    const response = await fetch(url, {
      method: 'POST',
      headers,
      signal,
      credentials: withCredentials ? 'include' : 'same-origin',
      body: skipSnake ? JSON.stringify(body) : JSON.stringify(snake(body)),
    });
    await Controller.handleFailedResponse(response);
    if (response.status === 204) {
      return null;
    }
    const responseJson = await response.json();
    return camelize(responseJson);
  }

  static async patch(
    route: string,
    body,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
    signal = null,
  ) {
    const url = Controller.makeUrl(route, args);
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      signal,
      body: Array.isArray(body)
        ? JSON.stringify(body.map(e => snake(e)))
        : JSON.stringify(snake(body)),
    });
    const responseJson = await response.json();
    return camelize(responseJson);
  }

  static delete(
    route: string,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
    signal = null,
    body: any = undefined,
  ) {
    const url = Controller.makeUrl(route, args);
    return fetch(url, {
      method: 'DELETE',
      headers,
      signal,
      body: Array.isArray(body)
        ? JSON.stringify(body.map(e => snake(e)))
        : JSON.stringify(snake(body)),
    }).then(response => Controller.handleFailedResponse(response));
  }

  static deleteWithJsonResult(...params: Parameters<typeof Controller.delete>) {
    return this.delete(...params)
      .then(response => response.json().catch(() => response))
      .then(camelize);
  }

  static async handleFailedResponse(response) {
    if (!response.ok) {
      throw response;
    }
    return response;
  }

  static makeUrl(route, args) {
    let url = route;
    if (args !== null) {
      url = `${route}?${Controller.makeQueryString(args)}`;
    }

    return url;
  }

  static makeQueryString(obj) {
    return Object.keys(obj)
      .map(key => `${encodeURIComponent(key)}=${obj[key]}`)
      .join('&');
  }
}
